import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Navbar } from "../components/Navbar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Play,
  FileText,
  Image,
  Music,
  Film,
  Upload,
  Trash2,
  Edit,
  Plus,
  Clock,
  Lock,
  Unlock,
  Download,
  Eye,
  Key,
  BookOpen,
  Video,
  Headphones,
  ExternalLink,
  Youtube,
  ArrowUp,
  ArrowDown,
  ChevronsUp,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${window.location.origin}/api`;
const BASE_URL = window.location.origin;

// Helper per costruire URL completo per le immagini
const getFullUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BASE_URL}${url}`;
};

// Categorie (unificate: nessuna distinzione free/premium - chi è loggato vede tutto)
const CATEGORIE = [
  { id: "video", label: "Video Formativi", icon: Video },
  { id: "podcast", label: "Podcast", icon: Music },
  { id: "dispense", label: "Dispense", icon: BookOpen },
];

// Durate video (da 1 a 130 minuti)
const DURATE_VIDEO = [
  "1 minuto", "2 minuti", "3 minuti", "5 minuti", "10 minuti", "15 minuti",
  "20 minuti", "25 minuti", "30 minuti", "35 minuti", "40 minuti", "45 minuti",
  "50 minuti", "55 minuti", "60 minuti", "65 minuti", "70 minuti", "75 minuti",
  "80 minuti", "85 minuti", "90 minuti", "95 minuti", "100 minuti",
  "105 minuti", "110 minuti", "115 minuti", "120 minuti", "125 minuti", "130 minuti"
];

// Durate podcast (da 1 a 130 minuti)
const DURATE_PODCAST = [
  "1 minuto", "2 minuti", "3 minuti", "5 minuti", "10 minuti", "15 minuti",
  "20 minuti", "25 minuti", "30 minuti", "35 minuti", "40 minuti", "45 minuti",
  "50 minuti", "55 minuti", "60 minuti", "65 minuti", "70 minuti", "75 minuti",
  "80 minuti", "85 minuti", "90 minuti", "95 minuti", "100 minuti",
  "105 minuti", "110 minuti", "115 minuti", "120 minuti", "125 minuti", "130 minuti"
];

// Piattaforme podcast
const PIATTAFORME_PODCAST = [
  { id: "upload", label: "Carica File Audio (MP3)" },
  { id: "spotify", label: "Link Spotify" },
  { id: "apple", label: "Link Apple Podcast" },
  { id: "youtube", label: "Link YouTube" },
  { id: "altro", label: "Altro Link" },
];

const getFileIcon = (fileType) => {
  if (!fileType) return FileText;
  if (fileType.includes("video") || ["mp4", "avi", "mov", "wmv"].some(ext => fileType.includes(ext))) return Film;
  if (fileType.includes("audio") || ["mp3", "wav", "ogg"].some(ext => fileType.includes(ext))) return Music;
  if (fileType.includes("image") || ["jpg", "jpeg", "png", "gif", "webp"].some(ext => fileType.includes(ext))) return Image;
  return FileText;
};

const MaterialiClienti = () => {
  const { user } = useAuth();
  const [materiali, setMateriali] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [passwordInput, setPasswordInput] = useState("");
  const [unlockedItems, setUnlockedItems] = useState(new Set());
  const [activeTab, setActiveTab] = useState("video");
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    categoria: "",
    durata: "",
    password: "",
    file: null,
    piattaforma: "upload",
    link_esterno: "",
    copertina: null,
    video_source: "upload", // "upload" o "youtube"
    youtube_url: "",
  });

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    fetchMateriali();
    // Load unlocked items from localStorage
    const saved = localStorage.getItem("unlockedMateriali");
    if (saved) {
      setUnlockedItems(new Set(JSON.parse(saved)));
    }
  }, []);

  const fetchMateriali = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/clienti/materiali`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMateriali(data);
      }
    } catch (error) {
      console.error("Errore caricamento materiali:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500 * 1024 * 1024) {
        toast.error("File troppo grande. Massimo 500MB");
        return;
      }
      setFormData({ ...formData, file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.categoria) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    const isPremium = formData.categoria.includes("premium");
    if (isPremium && !formData.password && !editingMaterial) {
      toast.error("I contenuti premium richiedono una password");
      return;
    }

    const isPodcast = formData.categoria.includes("podcast");
    const usaLinkEsterno = isPodcast && formData.piattaforma !== "upload";
    
    // Gestione video YouTube
    const isVideo = formData.categoria.includes("video");
    const usaYouTube = isVideo && formData.video_source === "youtube";

    // Validazione YouTube per video
    if (usaYouTube) {
      if (!formData.youtube_url) {
        toast.error("Inserisci il link YouTube");
        return;
      }
      const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/)|youtu\.be\/)[a-zA-Z0-9_-]+/;
      if (!youtubeRegex.test(formData.youtube_url)) {
        toast.error("Inserisci un link YouTube valido");
        return;
      }
    }

    if (!editingMaterial && !usaLinkEsterno && !usaYouTube && !formData.file) {
      toast.error("Seleziona un file da caricare o inserisci un link YouTube");
      return;
    }

    if (usaLinkEsterno && !formData.link_esterno) {
      toast.error("Inserisci il link del podcast");
      return;
    }

    setUploading(true);
    const token = localStorage.getItem("token");

    try {
      let fileUrl = editingMaterial?.file_url;
      let fileType = editingMaterial?.file_type;
      let copertinaUrl = editingMaterial?.copertina_url;

      // Upload copertina se presente
      if (formData.copertina) {
        const copertinaData = new FormData();
        copertinaData.append("file", formData.copertina);

        try {
          const copertinaRes = await fetch(`${API}/upload`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: copertinaData,
          });

          if (copertinaRes.ok) {
            const copertinaResult = await copertinaRes.json();
            copertinaUrl = copertinaResult.url;
          } else {
            const errText = await copertinaRes.text();
            console.error("Errore upload copertina:", errText);
            toast.error("Errore nel caricamento della copertina");
          }
        } catch (err) {
          console.error("Errore upload copertina:", err);
          toast.error("Errore nel caricamento della copertina");
        }
      }

      if (usaLinkEsterno) {
        fileUrl = formData.link_esterno;
        fileType = `link-${formData.piattaforma}`;
      } else if (usaYouTube) {
        // Video YouTube
        fileUrl = formData.youtube_url;
        fileType = "youtube";
      } else if (formData.file) {
        // Pre-check size (frontend)
        if (formData.file.size > 500 * 1024 * 1024) {
          throw new Error("File troppo grande (max 500MB). Carica il video su YouTube e usa l'opzione 'Link YouTube'.");
        }
        const uploadData = new FormData();
        uploadData.append("file", formData.file);

        const uploadRes = await fetch(`${API}/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: uploadData,
        });

        if (!uploadRes.ok) {
          if (uploadRes.status === 413) {
            throw new Error("File troppo grande (limite di rete: 500MB). Carica il video su YouTube e usa 'Link YouTube'.");
          }
          let errMsg = "Errore upload file";
          try {
            const err = await uploadRes.json();
            if (err?.detail) errMsg = err.detail;
          } catch {}
          throw new Error(errMsg);
        }
        const uploadResult = await uploadRes.json();
        fileUrl = uploadResult.url;
        fileType = formData.file.type || formData.file.name.split('.').pop();
      }

      const materialData = {
        name: formData.name,
        description: formData.description,
        categoria: formData.categoria,
        durata: formData.durata,
        password: formData.password || editingMaterial?.password || "",
        file_url: fileUrl,
        file_type: fileType,
        piattaforma: isPodcast ? formData.piattaforma : (usaYouTube ? "youtube" : null),
        link_esterno: usaLinkEsterno ? formData.link_esterno : (usaYouTube ? formData.youtube_url : null),
        copertina_url: copertinaUrl || null,
        youtube_url: usaYouTube ? formData.youtube_url : null,
        video_source: isVideo ? (usaYouTube ? "youtube" : "upload") : null,
      };

      const method = editingMaterial ? "PUT" : "POST";
      const url = editingMaterial 
        ? `${API}/clienti/materiali/${editingMaterial.material_id}`
        : `${API}/clienti/materiali`;

      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(materialData),
      });

      if (res.ok) {
        toast.success(editingMaterial ? "Materiale aggiornato!" : "Materiale caricato!");
        setDialogOpen(false);
        resetForm();
        fetchMateriali();
      } else {
        throw new Error("Errore salvataggio");
      }
    } catch (error) {
      toast.error("Errore: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (materialId) => {
    if (!window.confirm("Eliminare questo materiale?")) return;

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/clienti/materiali/${materialId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        toast.success("Materiale eliminato");
        fetchMateriali();
      }
    } catch (error) {
      toast.error("Errore eliminazione");
    }
  };

  const handleMove = async (materialId, direction) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/admin/materiali/${materialId}/order`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ direction }),
      });
      if (res.ok) {
        await fetchMateriali();
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || "Errore spostamento");
      }
    } catch (e) {
      toast.error("Errore di rete");
    }
  };

  const handleUnlock = (material) => {
    setSelectedMaterial(material);
    setPasswordInput("");
    setPasswordDialogOpen(true);
  };

  // Registra una visualizzazione (chi/quante) - non blocca l'apertura se fallisce
  const trackView = async (material) => {
    if (!material?.material_id || isAdmin) return; // non contiamo le view dell'admin
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      await fetch(`${API}/clienti/materiali/${material.material_id}/view`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {
      // silent
    }
  };

  // Mostra modal con elenco utenti che hanno visto il materiale (admin only)
  const [viewsDialogOpen, setViewsDialogOpen] = useState(false);
  const [viewsLoading, setViewsLoading] = useState(false);
  const [viewsData, setViewsData] = useState(null);

  const openViewsDialog = async (material) => {
    setSelectedMaterial(material);
    setViewsDialogOpen(true);
    setViewsLoading(true);
    setViewsData(null);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/admin/materiali/${material.material_id}/views`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setViewsData(data);
      }
    } catch (e) {
      toast.error("Errore caricamento visualizzazioni");
    } finally {
      setViewsLoading(false);
    }
  };

  const verifyPassword = async () => {
    if (!selectedMaterial) return;

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/clienti/materiali/${selectedMaterial.material_id}/verify`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ password: passwordInput }),
      });

      if (res.ok) {
        const newUnlocked = new Set(unlockedItems);
        newUnlocked.add(selectedMaterial.material_id);
        setUnlockedItems(newUnlocked);
        localStorage.setItem("unlockedMateriali", JSON.stringify([...newUnlocked]));
        toast.success("Contenuto sbloccato!");
        setPasswordDialogOpen(false);
      } else {
        toast.error("Password errata");
      }
    } catch (error) {
      toast.error("Errore verifica password");
    }
  };

  const resetForm = () => {
    setFormData({ name: "", description: "", categoria: "", durata: "", password: "", file: null, piattaforma: "upload", link_esterno: "", copertina: null, video_source: "upload", youtube_url: "" });
    setEditingMaterial(null);
  };

  const openEditDialog = (material) => {
    setEditingMaterial(material);
    const isYouTube = material.file_type === "youtube";
    setFormData({
      name: material.name,
      description: material.description || "",
      categoria: material.categoria,
      durata: material.durata || "",
      password: "",
      file: null,
      piattaforma: material.piattaforma || "upload",
      link_esterno: material.link_esterno || "",
      copertina: null,
      video_source: isYouTube ? "youtube" : "upload",
      youtube_url: isYouTube ? (material.link_esterno || material.file_url || "") : "",
    });
    setDialogOpen(true);
  };

  const isUnlocked = (_material) => {
    // Login = accesso a tutti i contenuti (Biblioteca Accademia ODR-ITA318)
    return true;
  };

  const getMaterialiByCategoria = (categoriaId) => {
    // Backward compat: include materiali con vecchie categorie -free / -premium
    return materiali.filter(m =>
      m.categoria === categoriaId ||
      m.categoria === `${categoriaId}-free` ||
      m.categoria === `${categoriaId}-premium`
    );
  };

  // Estrae l'ID YouTube da un URL
  const extractYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([\w-]{11})/);
    return match ? match[1] : null;
  };

  // Modal player YouTube
  const [youtubeModalOpen, setYoutubeModalOpen] = useState(false);
  const [youtubeVideoId, setYoutubeVideoId] = useState(null);

  const playYouTube = (videoId, material) => {
    setYoutubeVideoId(videoId);
    setYoutubeModalOpen(true);
    trackView(material);
  };

  const renderMaterialCard = (material) => {
    const FileIcon = getFileIcon(material.file_type);
    const unlocked = isUnlocked(material);
    const isPremium = material.categoria.includes("premium");

    // È un video YouTube? (rileva anche video legacy salvati senza video_source)
    const youtubeUrl = material.youtube_url || material.link_esterno || material.file_url || "";
    const looksLikeYouTube =
      material.video_source === "youtube" ||
      material.piattaforma === "youtube" ||
      material.file_type === "youtube" ||
      /(?:youtu\.be|youtube\.com)/i.test(youtubeUrl);
    const youtubeId = looksLikeYouTube ? extractYouTubeId(youtubeUrl) : null;
    const isYouTubeVideo = !!youtubeId;
    // Copertina: se non c'è custom, usa thumbnail YouTube
    const coverUrl = material.copertina_url
      ? getFullUrl(material.copertina_url)
      : isYouTubeVideo
        ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`
        : null;

    return (
      <div
        key={material.material_id}
        className={`bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all border-2 ${
          isPremium ? (unlocked ? "border-green-200" : "border-amber-200") : "border-gray-100"
        }`}
      >
        {/* Copertina (custom o YouTube thumbnail) */}
        {coverUrl && (
          <div
            className={`relative aspect-video bg-gray-900 ${isYouTubeVideo && unlocked ? "cursor-pointer group" : ""}`}
            onClick={() => isYouTubeVideo && unlocked && playYouTube(youtubeId, material)}
            data-testid={isYouTubeVideo ? `youtube-cover-${material.material_id}` : undefined}
          >
            <img
              src={coverUrl}
              alt={material.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                if (isYouTubeVideo) {
                  e.target.src = `https://img.youtube.com/vi/${youtubeId}/0.jpg`;
                }
              }}
            />
            {isYouTubeVideo && unlocked && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                  <Play className="w-8 h-8 text-[#7A4856] ml-1 fill-current" />
                </div>
              </div>
            )}
            {isYouTubeVideo && (
              <span className="absolute top-2 right-2 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded">YouTube</span>
            )}
            {isPremium && !unlocked && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <Lock className="w-10 h-10 text-white/80" />
              </div>
            )}
          </div>
        )}
        <div className="p-5">
          <div className="flex items-start gap-4">
            {!material.copertina_url && (
              <div className={`p-3 rounded-xl ${isPremium ? "bg-amber-100" : "bg-rose-100"}`}>
                <FileIcon className={`w-8 h-8 ${isPremium ? "text-amber-600" : "text-[#B87A8A]"}`} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold text-gray-800 truncate">{material.name}</h4>
                {isPremium && (
                  unlocked ? (
                    <Unlock className="w-4 h-4 text-green-500" />
                  ) : (
                    <Lock className="w-4 h-4 text-amber-500" />
                  )
                )}
              </div>
              {material.durata && (
                <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                  <Clock className="w-3 h-3" />
                  <span>{material.durata}</span>
                </div>
              )}
              {material.description && (
                <p className="text-sm text-gray-500 mt-2 line-clamp-2">{material.description}</p>
              )}
              
              <div className="flex items-center gap-2 mt-4">
                {unlocked ? (
                  isYouTubeVideo ? (
                    <button
                      onClick={() => playYouTube(youtubeId, material)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-[#7A4856] text-white rounded-full text-sm hover:bg-[#5A3340] transition-colors"
                      data-testid={`play-youtube-${material.material_id}`}
                    >
                      <Play className="w-4 h-4 fill-current" /> Riproduci
                    </button>
                  ) : (
                  <>
                    <a
                      href={getFullUrl(material.file_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackView(material)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-[#B87A8A] text-white rounded-full text-sm hover:bg-[#a06978] transition-colors"
                      data-testid={`play-material-${material.material_id}`}
                    >
                      {material.file_type?.includes("video") ? (
                      <><Play className="w-4 h-4" /> Riproduci</>
                    ) : (
                      <><Eye className="w-4 h-4" /> Visualizza</>
                    )}
                  </a>
                  <a
                    href={getFullUrl(material.file_url)}
                    download
                    onClick={() => trackView(material)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-full text-sm hover:bg-gray-200 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                </>
                  )
              ) : (
                <Button
                  size="sm"
                  onClick={() => handleUnlock(material)}
                  className="rounded-full bg-amber-500 hover:bg-amber-600"
                >
                  <Key className="w-4 h-4 mr-1" /> Inserisci Password
                </Button>
              )}
              </div>
          </div>
          
          {/* Admin actions - VISIBILI */}
          {isAdmin && (
            <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-gray-100 flex-wrap">
              <button
                onClick={() => handleMove(material.material_id, "top")}
                className="flex items-center gap-1 px-2.5 py-1.5 text-amber-700 hover:bg-amber-50 rounded-full text-xs font-medium transition-colors border border-amber-200"
                data-testid={`top-material-${material.material_id}`}
                title="Metti in cima"
              >
                <ChevronsUp className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => handleMove(material.material_id, "up")}
                className="flex items-center gap-1 px-2.5 py-1.5 text-gray-700 hover:bg-gray-100 rounded-full text-xs font-medium transition-colors border border-gray-200"
                data-testid={`up-material-${material.material_id}`}
                title="Sposta su"
              >
                <ArrowUp className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => handleMove(material.material_id, "down")}
                className="flex items-center gap-1 px-2.5 py-1.5 text-gray-700 hover:bg-gray-100 rounded-full text-xs font-medium transition-colors border border-gray-200"
                data-testid={`down-material-${material.material_id}`}
                title="Sposta giù"
              >
                <ArrowDown className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => openViewsDialog(material)}
                className="flex items-center gap-1 px-3 py-1.5 text-purple-600 hover:bg-purple-50 rounded-full text-xs font-medium transition-colors border border-purple-200"
                data-testid={`views-material-${material.material_id}`}
              >
                <Eye className="w-3.5 h-3.5" />
                {(material.views_total ?? 0)} viste
              </button>
              <button
                onClick={() => openEditDialog(material)}
                className="flex items-center gap-1 px-3 py-1.5 text-blue-600 hover:bg-blue-50 rounded-full text-xs font-medium transition-colors border border-blue-200"
                data-testid={`edit-material-${material.material_id}`}
              >
                <Edit className="w-3.5 h-3.5" /> Modifica
              </button>
              <button
                onClick={() => handleDelete(material.material_id)}
                className="flex items-center gap-1 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-full text-xs font-medium transition-colors border border-red-200"
                data-testid={`delete-material-${material.material_id}`}
              >
                <Trash2 className="w-3.5 h-3.5" /> Elimina
              </button>
            </div>
          )}
        </div>
        </div>
      </div>
    );
  };

  const getPlatformIcon = (piattaforma) => {
    switch (piattaforma) {
      case "spotify":
        return (
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
            <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
          </svg>
        );
      case "apple":
        return (
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
          </svg>
        );
      case "youtube":
        return (
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
          </svg>
        );
      default:
        return <Headphones className="w-5 h-5" />;
    }
  };

  const getPlatformColor = (piattaforma) => {
    switch (piattaforma) {
      case "spotify": return "bg-green-500 hover:bg-green-600";
      case "apple": return "bg-purple-500 hover:bg-purple-600";
      case "youtube": return "bg-red-500 hover:bg-red-600";
      default: return "bg-[#B87A8A] hover:bg-[#a06978]";
    }
  };

  const getPlatformLabel = (piattaforma) => {
    switch (piattaforma) {
      case "spotify": return "Ascolta su Spotify";
      case "apple": return "Ascolta su Apple Podcast";
      case "youtube": return "Ascolta su YouTube";
      default: return "Ascolta";
    }
  };

  const renderPodcastCard = (material) => {
    const unlocked = isUnlocked(material);
    const isPremium = material.categoria.includes("premium");
    const isExternalLink = material.piattaforma && material.piattaforma !== "upload";

    return (
      <div
        key={material.material_id}
        className={`bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all border ${
          isPremium ? (unlocked ? "border-green-200" : "border-amber-200") : "border-gray-100"
        }`}
      >
        {/* Copertina se presente */}
        {material.copertina_url && (
          <div className="relative aspect-square bg-gray-100">
            <img 
              src={getFullUrl(material.copertina_url)} 
              alt={material.name}
              className="w-full h-full object-cover"
            />
            {isPremium && !unlocked && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <Lock className="w-6 h-6 text-white/80" />
              </div>
            )}
            {isExternalLink && (
              <div className="absolute bottom-1 right-1">
                <span className={`p-1.5 rounded-full bg-white/90 shadow-sm text-xs ${
                  material.piattaforma === 'spotify' ? 'text-green-500' : 
                  material.piattaforma === 'apple' ? 'text-purple-500' : 
                  material.piattaforma === 'youtube' ? 'text-red-500' : 'text-gray-500'
                }`}>
                  {getPlatformIcon(material.piattaforma)}
                </span>
              </div>
            )}
          </div>
        )}
        <div className="p-3">
          {/* Icona solo se non c'è copertina */}
          {!material.copertina_url && (
            <div className={`p-2 rounded-lg mb-2 inline-block ${isPremium ? "bg-amber-100" : "bg-purple-100"}`}>
              {isExternalLink ? (
                <span className={`text-sm ${isPremium ? "text-amber-600" : "text-purple-600"}`}>
                  {getPlatformIcon(material.piattaforma)}
                </span>
              ) : (
                <Headphones className={`w-5 h-5 ${isPremium ? "text-amber-600" : "text-purple-600"}`} />
              )}
            </div>
          )}
          
          {/* Titolo e info */}
          <div className="flex items-center gap-1 mb-1">
            <h4 className="font-semibold text-gray-800 text-sm truncate flex-1">{material.name}</h4>
            {isPremium && (
              unlocked ? (
                <Unlock className="w-3 h-3 text-green-500 shrink-0" />
              ) : (
                <Lock className="w-3 h-3 text-amber-500 shrink-0" />
              )
            )}
          </div>
          
          {material.durata && (
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Clock className="w-3 h-3" />
              <span>{material.durata}</span>
            </div>
          )}
          
          {/* Pulsante azione */}
          <div className="mt-2">
            {unlocked ? (
              isExternalLink ? (
                <a
                  href={material.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackView(material)}
                  className={`flex items-center justify-center gap-1 px-2 py-1.5 text-white rounded-full text-xs transition-colors w-full ${getPlatformColor(material.piattaforma)}`}
                >
                  {getPlatformIcon(material.piattaforma)}
                  <span className="hidden sm:inline">{getPlatformLabel(material.piattaforma)}</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <div className="flex gap-1">
                  <a
                    href={getFullUrl(material.file_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackView(material)}
                    className="flex items-center justify-center gap-1 px-2 py-1.5 bg-purple-500 text-white rounded-full text-xs hover:bg-purple-600 transition-colors flex-1"
                  >
                    <Play className="w-3 h-3" /> <span className="hidden sm:inline">Ascolta</span>
                  </a>
                  <a
                    href={getFullUrl(material.file_url)}
                    download
                    onClick={() => trackView(material)}
                    className="flex items-center justify-center p-1.5 bg-gray-100 text-gray-600 rounded-full text-xs hover:bg-gray-200 transition-colors"
                  >
                    <Download className="w-3 h-3" />
                  </a>
                </div>
              )
            ) : (
              <Button
                size="sm"
                onClick={() => handleUnlock(material)}
                className="rounded-full bg-amber-500 hover:bg-amber-600 w-full text-xs h-7"
              >
                <Key className="w-3 h-3 mr-1" /> Password
              </Button>
            )}
          </div>
          
          {/* Admin actions - VISIBILI */}
          {isAdmin && (
            <div className="flex justify-end gap-1.5 mt-2 pt-2 border-t border-gray-100 flex-wrap">
              <button
                onClick={() => handleMove(material.material_id, "top")}
                className="flex items-center gap-1 px-2 py-1 text-amber-700 hover:bg-amber-50 rounded-full text-[10px] font-medium transition-colors border border-amber-200"
                data-testid={`top-podcast-${material.material_id}`}
                title="Metti in cima"
              >
                <ChevronsUp className="w-3 h-3" />
              </button>
              <button
                onClick={() => handleMove(material.material_id, "up")}
                className="flex items-center gap-1 px-2 py-1 text-gray-700 hover:bg-gray-100 rounded-full text-[10px] font-medium transition-colors border border-gray-200"
                data-testid={`up-podcast-${material.material_id}`}
                title="Sposta su"
              >
                <ArrowUp className="w-3 h-3" />
              </button>
              <button
                onClick={() => handleMove(material.material_id, "down")}
                className="flex items-center gap-1 px-2 py-1 text-gray-700 hover:bg-gray-100 rounded-full text-[10px] font-medium transition-colors border border-gray-200"
                data-testid={`down-podcast-${material.material_id}`}
                title="Sposta giù"
              >
                <ArrowDown className="w-3 h-3" />
              </button>
              <button
                onClick={() => openViewsDialog(material)}
                className="flex items-center gap-1 px-2 py-1 text-purple-600 hover:bg-purple-50 rounded-full text-[10px] font-medium transition-colors border border-purple-200"
                data-testid={`views-podcast-${material.material_id}`}
              >
                <Eye className="w-3 h-3" /> {(material.views_total ?? 0)}
              </button>
              <button
                onClick={() => openEditDialog(material)}
                className="flex items-center gap-1 px-2 py-1 text-blue-600 hover:bg-blue-50 rounded-full text-[10px] font-medium transition-colors border border-blue-200"
                data-testid={`edit-podcast-${material.material_id}`}
              >
                <Edit className="w-3 h-3" /> Mod
              </button>
              <button
                onClick={() => handleDelete(material.material_id)}
                className="flex items-center gap-1 px-2 py-1 text-red-600 hover:bg-red-50 rounded-full text-[10px] font-medium transition-colors border border-red-200"
                data-testid={`delete-podcast-${material.material_id}`}
              >
                <Trash2 className="w-3 h-3" /> Elimina
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white flex items-center justify-center pt-16 lg:pt-0">
        <Navbar />
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500"></div>
      </div>
    );
  }

  // Counts per la sezione hero (eyebrow stats)
  const counts = {
    video: getMaterialiByCategoria("video").length,
    podcast: getMaterialiByCategoria("podcast").length,
    dispense: getMaterialiByCategoria("dispense").length,
  };

  return (
    <div className="min-h-screen bg-[#FAF6F2] pt-16 lg:pt-0 lg:mr-64" data-testid="biblioteca-page">
      <Navbar />

      {/* HERO Accademia ODR ITA 318 */}
      <section className="relative overflow-hidden">
        {/* Background layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#3A1F2A] via-[#5A3340] to-[#7A4856]" />
        <div className="absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: "radial-gradient(circle at 25% 30%, rgba(255,255,255,.7) 0, transparent 40%), radial-gradient(circle at 75% 70%, rgba(255,210,210,.6) 0, transparent 35%)"
        }} />
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60'%3E%3Cpath d='M0 30h60M30 0v60' stroke='%23ffffff' stroke-width='.5' fill='none'/%3E%3C/svg%3E\")"
        }} />

        <div className="relative max-w-6xl mx-auto px-4 py-12 sm:py-16 lg:py-20">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            {/* Logo */}
            <div className="bg-white rounded-3xl p-5 sm:p-6 shadow-2xl shrink-0" data-testid="accademia-logo-wrapper">
              <img
                src="/odr-assets/logo_odr_accademia.jpg"
                alt="Accademia ODR ITA 318"
                className="h-28 sm:h-32 lg:h-36 w-auto object-contain"
                data-testid="accademia-logo"
              />
            </div>

            {/* Texts */}
            <div className="flex-1 text-center lg:text-left">
              <p className="text-[#F5C9CD] text-xs sm:text-sm uppercase tracking-[0.3em] mb-3 font-medium">
                · Formazione Scientifica Ufficiale ·
              </p>
              <h1 className="text-white text-3xl sm:text-4xl lg:text-5xl font-light leading-tight" style={{ fontFamily: "Georgia, 'Playfair Display', serif" }}>
                <span className="block">Accademia ODR</span>
                <span className="block italic font-extralight text-[#FAD9DC]">La Bellezza che Cura</span>
              </h1>
              <div className="h-px w-24 bg-[#F5C9CD] my-5 mx-auto lg:mx-0" />
              <p className="text-[#FAE6E8] text-sm sm:text-base leading-relaxed max-w-xl mx-auto lg:mx-0">
                Lo spazio formativo riservato ai partner della linea Oncosmetics Derma Ritual.
                Videocorsi, podcast e dispense tecniche basate sui meccanismi d'azione della <em>Ioxina</em>.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 sm:gap-4 mt-7 max-w-md mx-auto lg:mx-0">
                {[
                  { label: "Video", value: counts.video, Icon: Film },
                  { label: "Podcast", value: counts.podcast, Icon: Music },
                  { label: "Dispense", value: counts.dispense, Icon: BookOpen },
                ].map(({ label, value, Icon }) => (
                  <div
                    key={label}
                    className="bg-white/8 backdrop-blur-sm border border-white/10 rounded-2xl p-3 sm:p-4 text-center"
                    data-testid={`stat-${label.toLowerCase()}`}
                  >
                    <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#F5C9CD] mx-auto mb-1.5" />
                    <div className="text-white text-xl sm:text-2xl font-light">{value}</div>
                    <div className="text-[#F5C9CD] text-[10px] sm:text-xs uppercase tracking-wider">{label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Decorative bottom edge */}
        <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-[#FAF6F2] to-transparent pointer-events-none" />
      </section>

      {/* Strip "tre pilastri" */}
      <section className="max-w-6xl mx-auto px-4 -mt-2 sm:-mt-4 mb-8 sm:mb-10 relative z-10">
        <div className="bg-white rounded-2xl shadow-sm border border-rose-100/60 p-5 sm:p-6 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8">
          {[
            { Icon: Film, title: "Videocorsi ODR", desc: "Formazione operativa sull'utilizzo della linea, da 1 a 130 minuti." },
            { Icon: Music, title: "Podcast Scientifici", desc: "Approfondimenti sui meccanismi d'azione della Ioxina e l'approccio scientifico ODR." },
            { Icon: BookOpen, title: "Dispense Tecniche", desc: "Materiale formativo da consultare online. Al termine: test di comprensione." },
          ].map(({ Icon, title, desc }) => (
            <div key={title} className="flex gap-4 items-start">
              <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-[#F5DCDF] to-[#E8C4C9] flex items-center justify-center">
                <Icon className="w-5 h-5 text-[#7A4856]" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-800 text-sm sm:text-base">{title}</h3>
                <p className="text-xs sm:text-sm text-gray-500 mt-1 leading-relaxed">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 pb-12 sm:pb-16">
        {/* Admin Controls */}
        {isAdmin && (
          <div className="mb-6 flex justify-end">
            <Button
              onClick={() => {
                resetForm();
                setDialogOpen(true);
              }}
              className="bg-[#7A4856] hover:bg-[#5A3340] rounded-full shadow-md"
              data-testid="add-content-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Carica Nuovo Contenuto
            </Button>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 h-14 sm:h-16 rounded-2xl bg-white border border-rose-100 p-1.5 shadow-sm">
            <TabsTrigger
              value="video"
              className="rounded-xl text-sm sm:text-base data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#7A4856] data-[state=active]:to-[#5A3340] data-[state=active]:text-white data-[state=active]:shadow-md flex items-center justify-center gap-2 transition-all"
              data-testid="tab-video"
            >
              <Film className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Video</span>
            </TabsTrigger>
            <TabsTrigger
              value="podcast"
              className="rounded-xl text-sm sm:text-base data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#7A4856] data-[state=active]:to-[#5A3340] data-[state=active]:text-white data-[state=active]:shadow-md flex items-center justify-center gap-2 transition-all"
              data-testid="tab-podcast"
            >
              <Music className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Podcast</span>
            </TabsTrigger>
            <TabsTrigger
              value="dispense"
              className="rounded-xl text-sm sm:text-base data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#7A4856] data-[state=active]:to-[#5A3340] data-[state=active]:text-white data-[state=active]:shadow-md flex items-center justify-center gap-2 transition-all"
              data-testid="tab-dispense"
            >
              <BookOpen className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Dispense</span>
            </TabsTrigger>
          </TabsList>

          {/* Video Tab */}
          <TabsContent value="video" className="space-y-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-rose-100 p-2 rounded-xl shrink-0">
                  <Film className="w-5 h-5 sm:w-6 sm:h-6 text-[#B87A8A]" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-800">Videocorsi ODR</h2>
                  <p className="text-xs sm:text-sm text-gray-500">Formazione sulla linea Oncosmetics Derma Ritual</p>
                </div>
              </div>

              {getMaterialiByCategoria("video").length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {getMaterialiByCategoria("video").map(renderMaterialCard)}
                </div>
              ) : (
                <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 text-center text-gray-500">
                  <Film className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm sm:text-base">Nessun video disponibile al momento</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Dispense Tab */}
          <TabsContent value="dispense" className="space-y-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-rose-100 p-2 rounded-xl shrink-0">
                  <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-[#B87A8A]" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-800">Dispense Tecniche</h2>
                  <p className="text-xs sm:text-sm text-gray-500">Approfondimenti scientifici e materiale formativo da consultare online</p>
                </div>
              </div>

              {getMaterialiByCategoria("dispense").length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {getMaterialiByCategoria("dispense").map(renderMaterialCard)}
                </div>
              ) : (
                <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 text-center text-gray-500">
                  <BookOpen className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm sm:text-base">Nessuna dispensa disponibile al momento</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Podcast Tab */}
          <TabsContent value="podcast" className="space-y-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-rose-100 p-2 rounded-xl shrink-0">
                  <Music className="w-5 h-5 sm:w-6 sm:h-6 text-[#B87A8A]" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-800">Podcast Scientifici</h2>
                  <p className="text-xs sm:text-sm text-gray-500">Meccanismi d'azione della Ioxina e approccio scientifico ODR</p>
                </div>
              </div>

              {getMaterialiByCategoria("podcast").length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
                  {getMaterialiByCategoria("podcast").map(renderPodcastCard)}
                </div>
              ) : (
                <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 text-center text-gray-500">
                  <Music className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm sm:text-base">Nessun podcast disponibile al momento</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialog Upload */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingMaterial ? "Modifica Contenuto" : "Carica Nuovo Contenuto"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="name">Nome *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="es. Video Introduttivo"
                className="rounded-xl"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="categoria">Categoria *</Label>
              <Select
                value={formData.categoria}
                onValueChange={(value) => setFormData({ ...formData, categoria: value })}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Seleziona categoria" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIE.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      <div className="flex items-center gap-2">
                        {cat.premium ? <Lock className="w-4 h-4 text-amber-500" /> : <Unlock className="w-4 h-4 text-green-500" />}
                        {cat.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.categoria?.includes("video") && (
              <div className="space-y-2">
                <Label htmlFor="durata">Durata Video</Label>
                <Select
                  value={formData.durata}
                  onValueChange={(value) => setFormData({ ...formData, durata: value })}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Seleziona durata" />
                  </SelectTrigger>
                  <SelectContent>
                    {DURATE_VIDEO.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Selezione fonte video: Upload o YouTube */}
            {formData.categoria?.includes("video") && (
              <div className="space-y-2">
                <Label>Fonte Video *</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-all ${
                      formData.video_source === "upload"
                        ? "border-[#B87A8A] bg-[#B87A8A]/10 text-[#B87A8A]"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    onClick={() => setFormData({ ...formData, video_source: "upload", youtube_url: "" })}
                  >
                    <Upload className="w-5 h-5" />
                    <span className="text-sm font-medium">Carica File</span>
                  </button>
                  <button
                    type="button"
                    className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-all ${
                      formData.video_source === "youtube"
                        ? "border-red-500 bg-red-50 text-red-600"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    onClick={() => setFormData({ ...formData, video_source: "youtube", file: null })}
                  >
                    <Youtube className="w-5 h-5" />
                    <span className="text-sm font-medium">Link YouTube</span>
                  </button>
                </div>
              </div>
            )}

            {/* Input link YouTube per video */}
            {formData.categoria?.includes("video") && formData.video_source === "youtube" && (
              <div className="space-y-2">
                <Label htmlFor="youtube_url">Link Video YouTube *</Label>
                <div className="relative">
                  <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-600" />
                  <Input
                    id="youtube_url"
                    value={formData.youtube_url}
                    onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="rounded-xl pl-10"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Incolla il link del video YouTube (es. https://www.youtube.com/watch?v=xxxxx)
                </p>
              </div>
            )}

            {formData.categoria?.includes("podcast") && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="piattaforma">Fonte del Podcast *</Label>
                  <Select
                    value={formData.piattaforma}
                    onValueChange={(value) => setFormData({ ...formData, piattaforma: value, link_esterno: "" })}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Seleziona fonte" />
                    </SelectTrigger>
                    <SelectContent>
                      {PIATTAFORME_PODCAST.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.piattaforma !== "upload" && (
                  <div className="space-y-2">
                    <Label htmlFor="link_esterno">Link {formData.piattaforma === "spotify" ? "Spotify" : formData.piattaforma === "apple" ? "Apple Podcast" : formData.piattaforma === "youtube" ? "YouTube" : "Esterno"} *</Label>
                    <Input
                      id="link_esterno"
                      value={formData.link_esterno}
                      onChange={(e) => setFormData({ ...formData, link_esterno: e.target.value })}
                      placeholder={formData.piattaforma === "spotify" ? "https://open.spotify.com/episode/..." : formData.piattaforma === "apple" ? "https://podcasts.apple.com/..." : "https://..."}
                      className="rounded-xl"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="durata">Durata Podcast</Label>
                  <Select
                    value={formData.durata}
                    onValueChange={(value) => setFormData({ ...formData, durata: value })}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Seleziona durata" />
                    </SelectTrigger>
                    <SelectContent>
                      {DURATE_PODCAST.map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {formData.categoria?.includes("premium") && (
              <div className="space-y-2">
                <Label htmlFor="password">
                  Password di Accesso {!editingMaterial && "*"}
                </Label>
                <Input
                  id="password"
                  type="text"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Password per sbloccare il contenuto"
                  className="rounded-xl"
                />
                {editingMaterial && (
                  <p className="text-xs text-gray-500">Lascia vuoto per mantenere la password attuale</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="description">Descrizione</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Breve descrizione del contenuto..."
                className="rounded-xl"
                rows={2}
              />
            </div>

            {/* File upload - nascosto se podcast con link esterno o video con YouTube */}
            {!(formData.categoria?.includes("podcast") && formData.piattaforma !== "upload") && 
             !(formData.categoria?.includes("video") && formData.video_source === "youtube") && (
              <div className="space-y-2">
                <Label htmlFor="file">
                  File {!editingMaterial && "*"}
                  <span className="text-xs text-gray-500 ml-2">(Max 500MB)</span>
                </Label>
                {formData.categoria?.includes("video") && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 flex gap-2 items-start">
                    <span className="text-amber-600 shrink-0 text-base leading-none">⚠️</span>
                    <span>
                      <strong>Video più grandi di 500MB?</strong> Caricali prima su <strong>YouTube</strong> (anche come "Non in elenco") e usa il pulsante <strong>"Link YouTube"</strong> qui sopra. È più veloce e affidabile.
                    </span>
                  </div>
                )}
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-3 text-center hover:border-[#B87A8A] transition-colors">
                  <input
                    type="file"
                    id="file"
                    onChange={handleFileChange}
                    className="hidden"
                    accept={formData.categoria?.includes("podcast") ? "audio/*,.mp3,.wav,.ogg,.m4a" : "video/*,audio/*,image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"}
                  />
                  <label htmlFor="file" className="cursor-pointer">
                    <Upload className="w-6 h-6 mx-auto mb-1 text-gray-400" />
                    {formData.file ? (
                      <p className="text-[#B87A8A] font-medium text-sm">
                        {formData.file.name}
                        <span className="block text-xs text-gray-500 mt-0.5">
                          {(formData.file.size / 1024 / 1024).toFixed(1)} MB
                          {formData.file.size > 500 * 1024 * 1024 && (
                            <span className="text-red-500 font-bold"> — TROPPO GRANDE! Usa YouTube</span>
                          )}
                        </span>
                      </p>
                    ) : (
                      <p className="text-gray-500 text-sm">
                        {formData.categoria?.includes("podcast")
                          ? "Clicca per caricare file audio (MP3, WAV)"
                          : "Clicca per selezionare un file"
                        }
                      </p>
                    )}
                  </label>
                </div>
              </div>
            )}

            {/* Copertina - per video e podcast */}
            {(formData.categoria?.includes("video") || formData.categoria?.includes("podcast")) && (
              <div className="space-y-2">
                <Label htmlFor="copertina">
                  Immagine Copertina
                  <span className="text-xs text-gray-500 ml-2">(opzionale - 1280x720px o 1:1)</span>
                </Label>
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-3 text-center hover:border-purple-400 transition-colors">
                  <input
                    type="file"
                    id="copertina"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        if (file.size > 10 * 1024 * 1024) {
                          toast.error("Immagine troppo grande. Massimo 10MB");
                          return;
                        }
                        setFormData({ ...formData, copertina: file });
                      }
                    }}
                    className="hidden"
                    accept="image/*,.jpg,.jpeg,.png,.webp"
                  />
                  <label htmlFor="copertina" className="cursor-pointer">
                    <Image className="w-6 h-6 mx-auto mb-1 text-gray-400" />
                    {formData.copertina ? (
                      <p className="text-purple-600 font-medium text-sm">{formData.copertina.name}</p>
                    ) : editingMaterial?.copertina_url ? (
                      <p className="text-green-600 font-medium text-sm">Copertina già caricata (clicca per sostituire)</p>
                    ) : (
                      <p className="text-gray-500 text-sm">Clicca per caricare copertina</p>
                    )}
                  </label>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-3 sticky bottom-0 bg-white pb-1">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setDialogOpen(false);
                  resetForm();
                }}
                className="flex-1 rounded-full"
              >
                Annulla
              </Button>
              <Button
                type="submit"
                disabled={uploading}
                className="flex-1 bg-[#B87A8A] hover:bg-[#a06978] rounded-full"
              >
                {uploading ? "Caricamento..." : editingMaterial ? "Salva" : "Carica"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" />
              Inserisci Password
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Questo contenuto è protetto. Inserisci la password che hai ricevuto per sbloccarlo.
            </p>
            <Input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              placeholder="Password"
              className="rounded-xl"
              onKeyDown={(e) => e.key === "Enter" && verifyPassword()}
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setPasswordDialogOpen(false)}
                className="flex-1 rounded-full"
              >
                Annulla
              </Button>
              <Button
                onClick={verifyPassword}
                className="flex-1 bg-amber-500 hover:bg-amber-600 rounded-full"
              >
                Sblocca
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Visualizzazioni materiale (admin) */}
      <Dialog open={viewsDialogOpen} onOpenChange={setViewsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-purple-600" />
              Visualizzazioni
            </DialogTitle>
            <DialogDescription>
              {selectedMaterial?.name || selectedMaterial?.title || ""}
            </DialogDescription>
          </DialogHeader>

          {viewsLoading ? (
            <div className="py-10 text-center text-gray-400 text-sm">Caricamento...</div>
          ) : viewsData ? (
            <div className="space-y-4">
              {/* Riepilogo */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-purple-50 border border-purple-100 rounded-2xl p-4 text-center">
                  <div className="text-3xl font-light text-purple-700">{viewsData.total_views}</div>
                  <div className="text-xs text-purple-600 uppercase tracking-wider mt-1">Visualizzazioni totali</div>
                </div>
                <div className="bg-rose-50 border border-rose-100 rounded-2xl p-4 text-center">
                  <div className="text-3xl font-light text-[#7A4856]">{viewsData.unique_users}</div>
                  <div className="text-xs text-[#7A4856] uppercase tracking-wider mt-1">Clienti unici</div>
                </div>
              </div>

              {/* Elenco utenti */}
              {viewsData.viewers && viewsData.viewers.length > 0 ? (
                <div className="border rounded-2xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                      <tr>
                        <th className="px-3 py-2 text-left">Cliente</th>
                        <th className="px-3 py-2 text-left">Centro</th>
                        <th className="px-3 py-2 text-center">Viste</th>
                        <th className="px-3 py-2 text-left">Ultima</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {viewsData.viewers.map((v) => (
                        <tr key={v.view_id} data-testid={`viewer-row-${v.user_id}`}>
                          <td className="px-3 py-2">
                            <div className="font-medium text-gray-800">{v.user_name || "—"}</div>
                            <div className="text-xs text-gray-400">{v.user_email}</div>
                          </td>
                          <td className="px-3 py-2 text-gray-600">{v.centro_estetico || "—"}</td>
                          <td className="px-3 py-2 text-center">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-semibold text-xs">
                              {v.view_count}×
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500">
                            {v.last_viewed_at ? new Date(v.last_viewed_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-8 text-center text-gray-400 text-sm">
                  <Eye className="w-10 h-10 mx-auto opacity-30 mb-2" />
                  Nessuna visualizzazione registrata ancora.
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 text-center text-gray-400 text-sm">Errore caricamento</div>
          )}
        </DialogContent>
      </Dialog>
      {/* Dialog: YouTube Player */}
      <Dialog open={youtubeModalOpen} onOpenChange={setYoutubeModalOpen}>
        <DialogContent className="max-w-4xl p-0 bg-black border-0 overflow-hidden" data-testid="youtube-player-modal">
          <div className="aspect-video w-full">
            {youtubeVideoId && (
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&rel=0`}
                title="Videocorso ODR"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaterialiClienti;
